import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox, Page, Spinner } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { numbersApi } from 'apis/numbers';
import { smsApi } from 'apis/sms';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { SmsEmpty } from './components/empty/smsEmpty';
import { SmsTable } from './components/table/smsTable';
export const SettingsSmsView = () => {
    const [smsTableData, setSmsTableData] = useState([]);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const { items: phoneNumbers, asyncStatus: phoneNumbersAsyncStatus, hasNextPage, hasPreviousPage, nextPage, previousPage, } = usePaginatedEndpoint({
        endpoint: numbersApi.endpoints.readNumbers,
        initialArg: { capability: 'sms', limit: 12 },
        itemsSelector: ({ data }) => data.numbers,
    });
    const [readSmsNumbersQueue, { data: numbersQueues, isFetching: numbersQueuesIsFetching, isSuccess: numbersQueuesIsSuccess, },] = smsApi.useLazyReadSmsNumbersQueuesQuery();
    // We debounce the loading state of both requests as the computed value would
    // otherwise transition to false after the phone numbers request succeeds just
    // before fetching the queues.
    useDebounce(() => setIsLoading(phoneNumbersAsyncStatus === 'pending' || numbersQueuesIsFetching), 1, [phoneNumbersAsyncStatus, numbersQueuesIsFetching]);
    // Kicks off the fetching of queues after the phone numbers are retrieved.
    useEffect(() => {
        if (phoneNumbers.length) {
            readSmsNumbersQueue({
                numbers: phoneNumbers.map(({ e164_format }) => e164_format),
            });
        }
    }, [phoneNumbers, readSmsNumbersQueue]);
    // Finishes initializing after the data has been fetched.
    useEffect(() => {
        if ((numbersQueuesIsSuccess && !numbersQueuesIsFetching) ||
            (phoneNumbersAsyncStatus === 'fulfilled' && !phoneNumbers.length)) {
            setIsInitializing(false);
        }
    }, [
        phoneNumbers,
        phoneNumbersAsyncStatus,
        numbersQueuesIsFetching,
        numbersQueuesIsSuccess,
    ]);
    // Creates the table data items whenever the constituent parts are ready. The
    // queues can come back with empty array, so we don't require that to be
    // present.
    useEffect(() => {
        if (phoneNumbers.length) {
            setSmsTableData(phoneNumbers.reduce((smsTableData, phoneNumber) => {
                var _a;
                return [
                    ...smsTableData,
                    {
                        phoneNumber,
                        queue: (_a = numbersQueues === null || numbersQueues === void 0 ? void 0 : numbersQueues.data.numberQueues.find(({ number }) => number === phoneNumber.e164_format)) === null || _a === void 0 ? void 0 : _a.queue,
                    },
                ];
            }, []));
        }
    }, [phoneNumbers, numbersQueues === null || numbersQueues === void 0 ? void 0 : numbersQueues.data]);
    /**
     * Handles whenever a sms table data item is changed in order to update the
     * sms table data with the changed item.
     */
    const handleSmsTableDataItemChanged = useCallback((updatedSmsTableDataItem) => {
        setSmsTableData((smsTableData) => smsTableData.map((smsTableDataItem) => smsTableDataItem.phoneNumber.e164_format ===
            updatedSmsTableDataItem.phoneNumber.e164_format
            ? updatedSmsTableDataItem
            : smsTableDataItem));
    }, []);
    return (_jsxs(Page, { children: [_jsx(Page.Head, { title: "Texts" }), _jsx(Page.Body, { children: isInitializing ? (_jsx(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", margin: "150px 0 0 0" }, { children: _jsx(Spinner, { size: 64 }) }))) : smsTableData.length === 0 ? (_jsx(SmsEmpty, {})) : (_jsx(SmsTable, { smsTableData: smsTableData, isLoading: isLoading, hasNextPage: hasNextPage, hasPreviousPage: hasPreviousPage, nextPage: nextPage, previousPage: previousPage, onSmsTableDataItemChanged: handleSmsTableDataItemChanged })) })] }));
};
